import { useEffect, useState } from 'react'
import { format, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import { Plus, X, Check, Ban, Clock, CalendarDays, Filter, AlertTriangle, BarChart3, RotateCcw, Settings, Pencil } from 'lucide-react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'

interface AbsenceType { id: string; name: string; code: string; color: string; isPaid: boolean; requiresBalance: boolean; defaultAcquiredPerYear: number }
interface Employee { id: string; firstName: string; lastName: string }
interface Absence {
  id: string; startDate: string; endDate: string; durationDays: number
  status: string; note: string | null; refusalReason: string | null; modificationNote: string | null
  employee: { id: string; firstName: string; lastName: string }
  absenceType: AbsenceType
  createdAt: string
}
interface AbsenceBalance {
  id: string; year: number; acquired: number; taken: number; anticipated: number; allowNegative: boolean
  employee: { id: string; firstName: string; lastName: string }
  absenceType: { id: string; name: string; code: string; color: string; requiresBalance: boolean }
}

const STATUS_LABEL: Record<string, { label: string; color: string; icon: any }> = {
  pending:                { label: 'En attente',          color: 'bg-amber-100 text-amber-800',   icon: Clock },
  approved:               { label: 'Approuvée',           color: 'bg-green-100 text-green-800',   icon: Check },
  refused:                { label: 'Refusée',             color: 'bg-red-100 text-red-800',       icon: Ban },
  cancelled:              { label: 'Annulée',             color: 'bg-gray-100 text-gray-500',     icon: X },
  modification_requested: { label: 'Modif. demandée',     color: 'bg-purple-100 text-purple-800', icon: RotateCcw },
}

const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

export default function Absences() {
  const [absences, setAbsences] = useState<Absence[]>([])
  const [types, setTypes] = useState<AbsenceType[]>([])
  const [employees, setEmployees] = useState<Employee[]>([])
  const [balances, setBalances] = useState<AbsenceBalance[]>([])
  const { current: establishment } = useEstablishment()
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState('pending')
  const [activeTab, setActiveTab] = useState<'list' | 'calendar' | 'balances' | 'types'>('list')
  const [modal, setModal] = useState(false)
  const [refuseModal, setRefuseModal] = useState<string | null>(null)
  const [modifModal, setModifModal] = useState<string | null>(null)
  const [refuseReason, setRefuseReason] = useState('')
  const [modifNote, setModifNote] = useState('')
  const [impactModal, setImpactModal] = useState<Absence | null>(null)
  const [impactShifts, setImpactShifts] = useState<Record<string, number>>({})
  const [saving, setSaving] = useState(false)
  const [understaffingAlerts, setUnderstaffingAlerts] = useState<any[]>([])
  const [form, setForm] = useState({ employeeId: '', absenceTypeId: '', startDate: '', endDate: '', note: '' })
  const [editAbsence, setEditAbsence] = useState<Absence | null>(null)
  const [editForm, setEditForm] = useState({ absenceTypeId: '', startDate: '', endDate: '', note: '' })
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}` })
  const [typeModal, setTypeModal] = useState(false)
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null)
  const [typeForm, setTypeForm] = useState({ name: '', code: '', color: '#6B7280', isPaid: true, requiresBalance: false, defaultAcquiredPerYear: 0 })
  const [balanceYear, setBalanceYear] = useState(new Date().getFullYear())
  const [initingBalances, setInitingBalances] = useState(false)

  async function load() {
    setLoading(true)
    try {
      const [absRes, typeRes, empRes] = await Promise.all([
        api.get(`/absences${filterStatus ? `?status=${filterStatus}` : ''}`),
        api.get('/absences/types'),
        api.get(`/employees${establishment?.id ? `?establishmentId=${establishment.id}` : ''}`),
      ])
      setAbsences(absRes.data)
      setTypes(typeRes.data)
      setEmployees(empRes.data)
      if (!form.employeeId && empRes.data.length > 0) setForm(f => ({ ...f, employeeId: empRes.data[0].id }))
      if (!form.absenceTypeId && typeRes.data.length > 0) setForm(f => ({ ...f, absenceTypeId: typeRes.data[0].id }))
    } finally { setLoading(false) }
  }

  async function loadBalances() {
    const estParam = establishment?.id ? `&establishmentId=${establishment.id}` : ''
    const res = await api.get(`/absences/balances?year=${balanceYear}${estParam}`)
    setBalances(res.data)
  }

  async function checkUnderstaffing() {
    try {
      const today = new Date().toISOString().split('T')[0]
      const res = await api.get(`/absences/understaffing?date=${today}&minStaff=2`)
      setUnderstaffingAlerts(res.data)
    } catch { /* ignore */ }
  }

  useEffect(() => { load(); checkUnderstaffing() }, [filterStatus])
  useEffect(() => { if (activeTab === 'balances') loadBalances() }, [activeTab, balanceYear])

  function calcDays() {
    if (!form.startDate || !form.endDate) return 0
    const start = new Date(form.startDate + 'T00:00:00')
    const end = new Date(form.endDate + 'T00:00:00')
    let count = 0
    const cur = new Date(start)
    while (cur <= end) { const d = cur.getDay(); if (d !== 0 && d !== 6) count++; cur.setDate(cur.getDate() + 1) }
    return count || 1
  }

  async function create() {
    if (!form.employeeId || !form.absenceTypeId || !form.startDate || !form.endDate) return
    setSaving(true)
    try {
      const res = await api.post('/absences', { ...form, establishmentId: establishment?.id })
      setAbsences(prev => [res.data, ...prev])
      setModal(false)
      setForm(f => ({ ...f, startDate: '', endDate: '', note: '' }))
    } catch (e: any) {
      alert(e.response?.data?.error || 'Erreur')
    } finally { setSaving(false) }
  }

  async function approve(id: string) {
    const res = await api.post(`/absences/${id}/approve`)
    setAbsences(prev => filterStatus === 'pending'
      ? prev.filter(a => a.id !== id)
      : prev.map(a => a.id === id ? res.data : a))
    setImpactModal(null)
  }

  async function checkImpact(absence: Absence) {
    setImpactModal(absence)
    setImpactShifts({})
    try {
      const res = await api.get(`/shifts?from=${absence.startDate}&to=${absence.endDate}`)
      const shifts: any[] = res.data
      // Count distinct employees scheduled per day (excluding this employee)
      const byDay: Record<string, Set<string>> = {}
      for (const s of shifts) {
        if (s.employeeId === absence.employee.id) continue
        if (!byDay[s.date]) byDay[s.date] = new Set()
        byDay[s.date].add(s.employeeId)
      }
      const counts: Record<string, number> = {}
      for (const [date, set] of Object.entries(byDay)) {
        counts[date] = set.size
      }
      setImpactShifts(counts)
    } catch { /* ignore */ }
  }

  async function refuse(id: string) {
    setSaving(true)
    try {
      const res = await api.post(`/absences/${id}/refuse`, { reason: refuseReason })
      setAbsences(prev => filterStatus === 'pending'
        ? prev.filter(a => a.id !== id)
        : prev.map(a => a.id === id ? res.data : a))
      setRefuseModal(null); setRefuseReason('')
    } finally { setSaving(false) }
  }

  async function requestModif(id: string) {
    if (!modifNote.trim()) return
    setSaving(true)
    try {
      const res = await api.post(`/absences/${id}/request-modification`, { note: modifNote })
      setAbsences(prev => prev.map(a => a.id === id ? res.data : a))
      setModifModal(null); setModifNote('')
    } finally { setSaving(false) }
  }

  async function deleteAbsence(id: string) {
    if (!confirm('Supprimer cette absence définitivement ?')) return
    await api.delete(`/absences/${id}`)
    setAbsences(prev => prev.filter(a => a.id !== id))
  }

  function openEdit(absence: Absence) {
    setEditAbsence(absence)
    setEditForm({ absenceTypeId: absence.absenceType.id, startDate: absence.startDate, endDate: absence.endDate, note: absence.note || '' })
  }

  function calcEditDays() {
    if (!editForm.startDate || !editForm.endDate) return 0
    const start = new Date(editForm.startDate + 'T00:00:00')
    const end = new Date(editForm.endDate + 'T00:00:00')
    let count = 0
    const cur = new Date(start)
    while (cur <= end) { const d = cur.getDay(); if (d !== 0 && d !== 6) count++; cur.setDate(cur.getDate() + 1) }
    return count || 1
  }

  async function saveEdit() {
    if (!editAbsence || !editForm.startDate || !editForm.endDate) return
    setSaving(true)
    try {
      const res = await api.put(`/absences/${editAbsence.id}`, editForm)
      setAbsences(prev => prev.map(a => a.id === editAbsence.id ? res.data : a))
      setEditAbsence(null)
    } catch (e: any) {
      alert(e.response?.data?.error || 'Erreur')
    } finally { setSaving(false) }
  }

  async function createType() {
    if (!typeForm.name || !typeForm.code) return
    setSaving(true)
    try {
      if (editingTypeId) {
        // Modification d'un type existant
        await api.put(`/absences/types/${editingTypeId}`, typeForm)
      } else {
        // Création d'un nouveau type
        await api.post('/absences/types', typeForm)
      }
      closeTypeModal()
      const res = await api.get('/absences/types')
      setTypes(res.data)
    } finally { setSaving(false) }
  }

  function openCreateType() {
    setEditingTypeId(null)
    setTypeForm({ name: '', code: '', color: '#6B7280', isPaid: true, requiresBalance: false, defaultAcquiredPerYear: 0 })
    setTypeModal(true)
  }

  function openEditType(t: AbsenceType) {
    setEditingTypeId(t.id)
    setTypeForm({
      name: t.name,
      code: t.code,
      color: t.color || '#6B7280',
      isPaid: t.isPaid,
      requiresBalance: (t as any).requiresBalance ?? false,
      defaultAcquiredPerYear: (t as any).defaultAcquiredPerYear ?? 0
    })
    setTypeModal(true)
  }

  function closeTypeModal() {
    setTypeModal(false)
    setEditingTypeId(null)
    setTypeForm({ name: '', code: '', color: '#6B7280', isPaid: true, requiresBalance: false, defaultAcquiredPerYear: 0 })
  }

  async function toggleTypeActive(type: AbsenceType) {
    await api.put(`/absences/types/${type.id}`, { isActive: !type.isActive })
    const res = await api.get('/absences/types')
    setTypes(res.data)
  }

  async function initBalances() {
    setInitingBalances(true)
    try {
      const res = await api.post('/absences/balances/init', { year: balanceYear })
      alert(`${res.data.created} soldes initialisés pour ${res.data.employees} employés / ${res.data.types} types`)
      loadBalances()
    } finally { setInitingBalances(false) }
  }

  async function updateBalance(id: string, field: 'acquired' | 'anticipated' | 'allowNegative', value: any) {
    await api.put(`/absences/balances/${id}`, { [field]: value })
    loadBalances()
  }

  const pendingCount = absences.filter(a => a.status === 'pending').length

  // ── Calendar view ──────────────────────────────────────────
  function CalendarView() {
    const [yr, mo] = calMonth.split('-').map(Number)
    const firstDay = new Date(yr, mo - 1, 1)
    const startDow = (firstDay.getDay() + 6) % 7
    const daysInMonth = new Date(yr, mo, 0).getDate()
    const cells = Array.from({ length: Math.ceil((startDow + daysInMonth) / 7) * 7 }, (_, i) => {
      const d = i - startDow + 1; return d >= 1 && d <= daysInMonth ? d : null
    })
    const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre']
    const DAYS_FR = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim']
    function absForDay(day: number) {
      const ds = `${yr}-${String(mo).padStart(2,'0')}-${String(day).padStart(2,'0')}`
      return absences.filter(a => a.status !== 'refused' && a.status !== 'cancelled' && a.startDate <= ds && a.endDate >= ds)
    }
    function prevMonth() { const d = new Date(yr, mo - 2, 1); setCalMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`) }
    function nextMonth() { const d = new Date(yr, mo, 1); setCalMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`) }
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <button onClick={prevMonth} className="px-2 py-1 hover:bg-gray-100 rounded text-gray-500">←</button>
          <h3 className="font-semibold text-gray-800">{MONTHS_FR[mo-1]} {yr}</h3>
          <button onClick={nextMonth} className="px-2 py-1 hover:bg-gray-100 rounded text-gray-500">→</button>
        </div>
        <div className="grid grid-cols-7 border-b border-gray-100">
          {DAYS_FR.map(d => <div key={d} className="text-center py-2 text-xs font-semibold text-gray-500 uppercase">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((day, i) => {
            const today = new Date()
            const isToday = day === today.getDate() && mo === today.getMonth()+1 && yr === today.getFullYear()
            const dayAbs = day ? absForDay(day) : []
            return (
              <div key={i} className={`min-h-[80px] border-r border-b border-gray-100 last:border-r-0 p-1 ${!day ? 'bg-gray-50/50' : ''}`}>
                {day && (
                  <>
                    <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-blue-600 text-white' : 'text-gray-600'}`}>{day}</div>
                    <div className="space-y-0.5">
                      {dayAbs.slice(0, 3).map(a => (
                        <div key={a.id} className="text-white text-xs px-1 py-0.5 rounded truncate" style={{ backgroundColor: a.absenceType.color }}>
                          {a.employee.firstName} — {a.absenceType.code}
                        </div>
                      ))}
                      {dayAbs.length > 3 && <div className="text-xs text-gray-400 px-1">+{dayAbs.length - 3}</div>}
                    </div>
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ── Balances view ──────────────────────────────────────────
  function BalancesView() {
    // Group by employee
    const byEmployee: Record<string, { emp: AbsenceBalance['employee']; rows: AbsenceBalance[] }> = {}
    for (const b of balances) {
      if (!byEmployee[b.employee.id]) byEmployee[b.employee.id] = { emp: b.employee, rows: [] }
      byEmployee[b.employee.id].rows.push(b)
    }
    const entries = Object.values(byEmployee)

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <select value={balanceYear} onChange={e => setBalanceYear(parseInt(e.target.value))} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
              {[new Date().getFullYear()-1, new Date().getFullYear(), new Date().getFullYear()+1].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <span className="text-sm text-gray-500">{balances.length} solde(s)</span>
          </div>
          <button onClick={initBalances} disabled={initingBalances}
            className="flex items-center gap-2 text-sm px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg">
            {initingBalances ? '...' : '⚡ Initialiser les soldes'}
          </button>
        </div>

        {entries.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 py-12 text-center text-gray-400 text-sm">
            <p>Aucun solde pour {balanceYear}.</p>
            <p className="mt-1">Cliquez sur "Initialiser les soldes" pour créer les compteurs automatiquement.</p>
          </div>
        ) : entries.map(({ emp, rows }) => (
          <div key={emp.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
              <span className="font-semibold text-gray-800">{emp.firstName} {emp.lastName}</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase tracking-wide border-b border-gray-100">
                  <th className="px-5 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-right">Acquis</th>
                  <th className="px-4 py-2 text-right">Anticipé</th>
                  <th className="px-4 py-2 text-right">Pris</th>
                  <th className="px-4 py-2 text-right font-bold">Restant</th>
                  <th className="px-4 py-2 text-center">Solde négatif</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(b => {
                  const remaining = b.acquired + b.anticipated - b.taken
                  return (
                    <tr key={b.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50">
                      <td className="px-5 py-2.5">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: b.absenceType.color }} />
                          {b.absenceType.name}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <input
                          type="number" step="0.5" min="0"
                          value={b.acquired}
                          onChange={e => updateBalance(b.id, 'acquired', parseFloat(e.target.value))}
                          className="w-16 text-right border border-gray-200 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                        />
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <input
                          type="number" step="0.5"
                          value={b.anticipated}
                          onChange={e => updateBalance(b.id, 'anticipated', parseFloat(e.target.value))}
                          className="w-16 text-right border border-gray-200 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                        />
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-600">{b.taken.toFixed(1)}</td>
                      <td className={`px-4 py-2.5 text-right font-bold ${remaining < 0 ? 'text-red-600' : remaining < 3 ? 'text-orange-500' : 'text-green-600'}`}>
                        {remaining.toFixed(1)}j
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <input type="checkbox" checked={b.allowNegative}
                          onChange={e => updateBalance(b.id, 'allowNegative', e.target.checked)}
                          className="w-4 h-4 rounded" />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    )
  }

  // ── Types view ─────────────────────────────────────────────
  function TypesView() {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <button onClick={openCreateType}
            className="flex items-center gap-2 text-sm px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
            <Plus size={15} /> Nouveau type
          </button>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Nom</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Code</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Payé</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Solde</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Jours/an</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Actif</th>
              </tr>
            </thead>
            <tbody>
              {types.map(t => (
                <tr key={t.id} className={`border-b border-gray-100 last:border-0 ${!t.isActive ? 'opacity-50' : ''}`}>
                  <td className="px-5 py-3">
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                      {t.name}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs bg-gray-50 rounded">{t.code}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${t.isPaid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {t.isPaid ? 'Oui' : 'Non'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${t.requiresBalance ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                      {t.requiresBalance ? 'Oui' : 'Non'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-600">
                    {t.defaultAcquiredPerYear > 0 ? `${t.defaultAcquiredPerYear}j` : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => toggleTypeActive(t)}
                        className={`text-xs px-2 py-0.5 rounded-full font-medium cursor-pointer ${t.isActive ? 'bg-green-100 text-green-700 hover:bg-red-50 hover:text-red-700' : 'bg-gray-100 text-gray-500 hover:bg-green-50 hover:text-green-700'}`}>
                        {t.isActive ? 'Actif' : 'Inactif'}
                      </button>
                      <button onClick={() => openEditType(t)}
                        title="Modifier ce type d'absence"
                        className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                        <Pencil size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <CalendarDays size={22} className="text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">Absences & Congés</h1>
          {pendingCount > 0 && (
            <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded-full">
              {pendingCount} en attente
            </span>
          )}
        </div>
        <button onClick={() => setModal(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
          <Plus size={16} /> Nouvelle demande
        </button>
      </div>

      {/* Sous-effectif alerts */}
      {understaffingAlerts.length > 0 && (
        <div className="mb-5 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <AlertTriangle size={18} className="text-orange-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-orange-800">Alerte sous-effectif aujourd'hui</p>
            {understaffingAlerts.map(a => (
              <p key={a.establishmentId} className="text-xs text-orange-700 mt-0.5">
                {a.establishmentName} : {a.available} employé(s) disponible(s) / {a.minRequired} minimum requis ({a.absences} absence(s) approuvée(s))
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1 w-fit">
        {([
          { key: 'list',     label: 'Liste',      icon: Filter },
          { key: 'calendar', label: 'Calendrier', icon: CalendarDays },
          { key: 'balances', label: 'Compteurs',  icon: BarChart3 },
          { key: 'types',    label: 'Types',      icon: Settings },
        ] as const).map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === key ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* List tab */}
      {activeTab === 'list' && (
        <>
          <div className="flex items-center gap-2 mb-4">
            {[
              { key: 'pending', label: 'En attente' },
              { key: 'approved', label: 'Approuvées' },
              { key: 'refused', label: 'Refusées' },
              { key: 'modification_requested', label: 'Modif. demandée' },
              { key: '', label: 'Toutes' },
            ].map(opt => (
              <button key={opt.key} onClick={() => setFilterStatus(opt.key)}
                className={`text-sm px-3 py-1.5 rounded-lg font-medium transition-colors ${filterStatus === opt.key ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                {opt.label}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="py-12 text-center text-gray-400 text-sm">Chargement...</div>
            ) : absences.length === 0 ? (
              <div className="py-12 text-center text-gray-400 text-sm">Aucune demande</div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Employé</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Période</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Durée</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {absences.map(absence => {
                    const st = STATUS_LABEL[absence.status] || STATUS_LABEL.pending
                    return (
                      <tr key={absence.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                              {absence.employee.firstName[0]}{absence.employee.lastName[0]}
                            </div>
                            <span className="text-sm font-medium text-gray-900">{absence.employee.firstName} {absence.employee.lastName}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <span className="inline-flex items-center gap-1.5 text-sm">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: absence.absenceType.color }} />
                            {absence.absenceType.name}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-600">
                          {format(parseISO(absence.startDate), 'd MMM', { locale: fr })}
                          {absence.startDate !== absence.endDate && <> → {format(parseISO(absence.endDate), 'd MMM yyyy', { locale: fr })}</>}
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-600">{absence.durationDays}j</td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${st.color}`}>
                            <st.icon size={11} /> {st.label}
                          </span>
                          {absence.refusalReason && (
                            <p className="text-xs text-gray-400 mt-0.5 max-w-[180px] truncate" title={absence.refusalReason}>{absence.refusalReason}</p>
                          )}
                          {absence.modificationNote && (
                            <p className="text-xs text-purple-500 mt-0.5 max-w-[180px] truncate" title={absence.modificationNote}>→ {absence.modificationNote}</p>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            {(absence.status === 'pending' || absence.status === 'modification_requested') && (
                              <>
                                <button onClick={() => checkImpact(absence)}
                                  className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 px-2.5 py-1.5 rounded-lg">
                                  <Check size={13} /> Approuver
                                </button>
                                <button onClick={() => { setRefuseModal(absence.id); setRefuseReason('') }}
                                  className="flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 px-2.5 py-1.5 rounded-lg">
                                  <Ban size={13} /> Refuser
                                </button>
                                {absence.status === 'pending' && (
                                  <button onClick={() => { setModifModal(absence.id); setModifNote('') }}
                                    className="flex items-center gap-1 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 px-2.5 py-1.5 rounded-lg">
                                    <RotateCcw size={13} /> Modif.
                                  </button>
                                )}
                              </>
                            )}
                            <button onClick={() => openEdit(absence)}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50" title="Modifier">
                              <Pencil size={14} />
                            </button>
                            <button onClick={() => deleteAbsence(absence.id)}
                              className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50" title="Supprimer">
                              <X size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {activeTab === 'calendar' && <CalendarView />}
      {activeTab === 'balances' && <BalancesView />}
      {activeTab === 'types' && <TypesView />}

      {/* Modal nouvelle demande */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-bold text-gray-900">Nouvelle demande d'absence</h2>
              <button onClick={() => setModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employé</label>
                <select value={form.employeeId} onChange={e => setForm(f => ({ ...f, employeeId: e.target.value }))} className={inputCls}>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type d'absence</label>
                <select value={form.absenceTypeId} onChange={e => setForm(f => ({ ...f, absenceTypeId: e.target.value }))} className={inputCls}>
                  {types.filter(t => t.isActive).map(t => <option key={t.id} value={t.id}>{t.name} ({t.code})</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
                  <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value, endDate: f.endDate || e.target.value }))} className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
                  <input type="date" value={form.endDate} min={form.startDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} className={inputCls} />
                </div>
              </div>
              {form.startDate && form.endDate && (
                <div className="text-sm text-blue-600 font-medium">{calcDays()} jour(s) ouvré(s)</div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (optionnel)</label>
                <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                  className={`${inputCls} resize-none`} rows={2} placeholder="Informations complémentaires..." />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setModal(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={create} disabled={saving || !form.employeeId || !form.startDate || !form.endDate}
                className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg">
                {saving ? 'Enregistrement...' : 'Créer la demande'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal refus */}
      {refuseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setRefuseModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-bold text-gray-900">Motif de refus</h2>
              <button onClick={() => setRefuseModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6">
              <textarea value={refuseReason} onChange={e => setRefuseReason(e.target.value)}
                className={`${inputCls} resize-none`} rows={3} placeholder="Raison du refus (optionnel)..." autoFocus />
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setRefuseModal(null)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={() => refuse(refuseModal)} disabled={saving}
                className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg">
                {saving ? '...' : 'Confirmer le refus'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal modification demandée */}
      {modifModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setModifModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-bold text-gray-900">Demander une modification</h2>
              <button onClick={() => setModifModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-500 mb-3">Précisez ce qui doit être modifié. L'employé sera notifié.</p>
              <textarea value={modifNote} onChange={e => setModifNote(e.target.value)}
                className={`${inputCls} resize-none`} rows={3} placeholder="Ex: Les dates chevauchent une période de forte activité..." autoFocus />
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setModifModal(null)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={() => requestModif(modifModal!)} disabled={saving || !modifNote.trim()}
                className="px-4 py-2 text-sm font-medium bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg">
                {saving ? '...' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal impact planning */}
      {impactModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setImpactModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-bold text-gray-900">Impact sur le planning</h2>
              <button onClick={() => setImpactModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: impactModal.absenceType.color }} />
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {impactModal.employee.firstName} {impactModal.employee.lastName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {impactModal.absenceType.name} · {format(parseISO(impactModal.startDate), 'd MMM', { locale: fr })} → {format(parseISO(impactModal.endDate), 'd MMM yyyy', { locale: fr })} · {impactModal.durationDays}j
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Effectif planifié par jour (hors employé absent) :</p>
                {Object.keys(impactShifts).length === 0 && (
                  <p className="text-sm text-gray-400 italic">Chargement de l'impact...</p>
                )}
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {(() => {
                    const days: string[] = []
                    const cur = new Date(impactModal.startDate + 'T00:00:00')
                    const end = new Date(impactModal.endDate + 'T00:00:00')
                    while (cur <= end) {
                      const d = cur.getDay()
                      if (d !== 0 && d !== 6) days.push(cur.toISOString().split('T')[0])
                      cur.setDate(cur.getDate() + 1)
                    }
                    return days.map(day => {
                      const count = impactShifts[day] ?? 0
                      const isLow = count < 2
                      return (
                        <div key={day} className={`flex items-center justify-between px-3 py-1.5 rounded-lg text-sm ${isLow ? 'bg-red-50' : 'bg-gray-50'}`}>
                          <span className={isLow ? 'text-red-700' : 'text-gray-700'}>
                            {format(parseISO(day), 'EEE d MMM', { locale: fr })}
                          </span>
                          <span className={`font-semibold ${isLow ? 'text-red-600' : 'text-green-600'}`}>
                            {count} employé{count !== 1 ? 's' : ''}
                            {isLow && ' ⚠️'}
                          </span>
                        </div>
                      )
                    })
                  })()}
                </div>
              </div>

              {Object.values(impactShifts).some(c => c < 2) && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700">
                    Certains jours seront en sous-effectif si vous approuvez cette absence.
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setImpactModal(null)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={() => approve(impactModal.id)} disabled={saving}
                className="px-4 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg">
                {saving ? '...' : 'Confirmer l\'approbation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal type */}
      {typeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeTypeModal}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-bold text-gray-900">{editingTypeId ? 'Modifier le type d\'absence' : 'Nouveau type d\'absence'}</h2>
              <button onClick={closeTypeModal} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input value={typeForm.name} onChange={e => setTypeForm(f => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="Ex: Congé paternité" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                  <input value={typeForm.code} onChange={e => setTypeForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} className={inputCls} placeholder="CPP" maxLength={5} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Couleur</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={typeForm.color} onChange={e => setTypeForm(f => ({ ...f, color: e.target.value }))} className="w-10 h-9 rounded cursor-pointer border border-gray-300" />
                    <span className="text-sm text-gray-500">{typeForm.color}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Jours acquis/an</label>
                  <input type="number" step="0.5" min="0" value={typeForm.defaultAcquiredPerYear}
                    onChange={e => setTypeForm(f => ({ ...f, defaultAcquiredPerYear: parseFloat(e.target.value) }))} className={inputCls} />
                </div>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={typeForm.isPaid} onChange={e => setTypeForm(f => ({ ...f, isPaid: e.target.checked }))} className="w-4 h-4 rounded" />
                  Absence payée
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={typeForm.requiresBalance} onChange={e => setTypeForm(f => ({ ...f, requiresBalance: e.target.checked }))} className="w-4 h-4 rounded" />
                  Vérifie le solde
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={closeTypeModal} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={createType} disabled={saving || !typeForm.name || !typeForm.code}
                className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg">
                {saving ? '...' : (editingTypeId ? 'Enregistrer' : 'Créer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal modifier une absence */}
      {editAbsence && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditAbsence(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="font-bold text-gray-900">Modifier l'absence</h2>
                <p className="text-xs text-gray-500 mt-0.5">{editAbsence.employee.firstName} {editAbsence.employee.lastName}</p>
              </div>
              <button onClick={() => setEditAbsence(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type d'absence</label>
                <select value={editForm.absenceTypeId} onChange={e => setEditForm(f => ({ ...f, absenceTypeId: e.target.value }))} className={inputCls}>
                  {types.filter(t => t.isActive).map(t => <option key={t.id} value={t.id}>{t.name} ({t.code})</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
                  <input type="date" value={editForm.startDate}
                    onChange={e => setEditForm(f => ({ ...f, startDate: e.target.value, endDate: f.endDate < e.target.value ? e.target.value : f.endDate }))}
                    className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
                  <input type="date" value={editForm.endDate} min={editForm.startDate}
                    onChange={e => setEditForm(f => ({ ...f, endDate: e.target.value }))}
                    className={inputCls} />
                </div>
              </div>
              {editForm.startDate && editForm.endDate && (
                <div className="text-sm text-blue-600 font-medium">{calcEditDays()} jour(s) ouvré(s)</div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (optionnel)</label>
                <textarea value={editForm.note} onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))}
                  className={`${inputCls} resize-none`} rows={2} />
              </div>
              {editAbsence.status === 'approved' && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
                  <AlertTriangle size={14} className="flex-shrink-0" />
                  Cette absence est approuvée — le solde sera ajusté automatiquement.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setEditAbsence(null)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={saveEdit} disabled={saving || !editForm.startDate || !editForm.endDate}
                className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg">
                {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
